import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Box, Button, Chip, Divider, Stack, TextField, Typography } from "@mui/material";
import { useEditorStore } from "@/features/scene-editor/editor-store";
import { autoRouteBetween, type FactoryScene, type ObjectKind, type SceneObject } from "@/features/simulation/public";

/**
 * 選択オブジェクトの詳細編集パネル。
 *
 * 共通: id / kind / 主要位置 (col,row) を編集
 * オブジェクト固有:
 *  - conveyor: bufferCapacity, isEntry, path 長さ (始点 + 末端調整)
 *  - press: conveyorId
 *  - arm: pickFromTile / placeToTile
 *  - transport: capacity, unloading, routeAutoMode (ON で auto-route 再計算)
 */

function findObject(scene: FactoryScene, kind: ObjectKind, id: string): SceneObject | null {
  switch (kind) {
    case "conveyor":
      return scene.conveyors.find((o) => o.id === id) ?? null;
    case "press":
      return scene.presses.find((o) => o.id === id) ?? null;
    case "arm":
      return scene.arms.find((o) => o.id === id) ?? null;
    case "transport":
      return scene.transports.find((o) => o.id === id) ?? null;
    case "sensor":
      return scene.sensors.find((o) => o.id === id) ?? null;
  }
}

export function ObjectPropertyPanel() {
  const draft = useEditorStore((s) => s.draft);
  const selection = useEditorStore((s) => s.selection);
  const moveObject = useEditorStore((s) => s.moveObject);
  const updateObject = useEditorStore((s) => s.updateObject);
  const removeObject = useEditorStore((s) => s.removeObject);

  if (!selection) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          オブジェクトを選択するか、パレットから新規配置してください
        </Typography>
      </Box>
    );
  }

  const obj = findObject(draft, selection.kind, selection.id);
  if (!obj) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          選択中のオブジェクトが存在しません
        </Typography>
      </Box>
    );
  }

  const ref = { kind: selection.kind, id: selection.id };
  const primaryTile = primaryTileOf(obj);

  const setPrimaryCol = (col: number) => moveObject(ref, { col, row: primaryTile.row });
  const setPrimaryRow = (row: number) => moveObject(ref, { col: primaryTile.col, row });

  return (
    <Stack sx={{ p: 2, gap: 1.5, height: "100%", overflow: "auto" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {obj.id}
        </Typography>
        <Chip size="small" label={obj.kind} />
      </Stack>

      <Stack direction="row" spacing={1}>
        <TextField
          label="col"
          type="number"
          size="small"
          value={primaryTile.col}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) setPrimaryCol(v);
          }}
        />
        <TextField
          label="row"
          type="number"
          size="small"
          value={primaryTile.row}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) setPrimaryRow(v);
          }}
        />
      </Stack>

      <Divider />

      {obj.kind === "conveyor" && (
        <>
          <TextField
            label="バッファ容量"
            type="number"
            size="small"
            value={obj.bufferCapacity}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (Number.isFinite(v)) updateObject(ref, { bufferCapacity: Math.max(1, Math.min(10, Math.round(v))) });
            }}
          />
          <TextField
            label="path 長 (タイル数)"
            type="number"
            size="small"
            value={obj.path.length}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isFinite(v) || v < 2) return;
              const target = Math.max(2, Math.min(20, Math.round(v)));
              const start = obj.path[0];
              if (!start) return;
              const newPath = [];
              for (let i = 0; i < target; i++) newPath.push({ col: start.col + i, row: start.row });
              updateObject(ref, { path: newPath });
            }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip
              size="small"
              color={obj.isEntry ? "primary" : "default"}
              label={obj.isEntry ? "投入起点" : "中継"}
              onClick={() => updateObject(ref, { isEntry: !obj.isEntry })}
              clickable
            />
            <Typography variant="caption" color="text.secondary">
              タップで切替
            </Typography>
          </Stack>
        </>
      )}

      {obj.kind === "press" && (
        <TextField
          select
          slotProps={{ select: { native: true } }}
          label="所属コンベア"
          size="small"
          value={obj.conveyorId}
          onChange={(e) => updateObject(ref, { conveyorId: e.target.value })}
        >
          {draft.conveyors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.id}
            </option>
          ))}
        </TextField>
      )}

      {obj.kind === "arm" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            ピック・プレース位置
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              label="pick col"
              type="number"
              size="small"
              value={obj.pickFromTile.col}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) updateObject(ref, { pickFromTile: { col: v, row: obj.pickFromTile.row } });
              }}
            />
            <TextField
              label="pick row"
              type="number"
              size="small"
              value={obj.pickFromTile.row}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) updateObject(ref, { pickFromTile: { col: obj.pickFromTile.col, row: v } });
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              label="place col"
              type="number"
              size="small"
              value={obj.placeToTile.col}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) updateObject(ref, { placeToTile: { col: v, row: obj.placeToTile.row } });
              }}
            />
            <TextField
              label="place row"
              type="number"
              size="small"
              value={obj.placeToTile.row}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v)) updateObject(ref, { placeToTile: { col: obj.placeToTile.col, row: v } });
              }}
            />
          </Stack>
        </>
      )}

      {obj.kind === "transport" && (
        <>
          <TransportRouteEditButtons transportId={obj.id} />
          <TextField
            label="積載上限"
            type="number"
            size="small"
            value={obj.capacity}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (Number.isFinite(v)) updateObject(ref, { capacity: Math.max(1, Math.min(10, Math.round(v))) });
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            積み下ろし位置
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              label="unload col"
              type="number"
              size="small"
              value={obj.unloading.col}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                const newUnload = { col: v, row: obj.unloading.row };
                const newRoute = obj.routeAutoMode ? autoRouteBetween(obj.parking, newUnload) : obj.route;
                updateObject(ref, { unloading: newUnload, route: newRoute });
              }}
            />
            <TextField
              label="unload row"
              type="number"
              size="small"
              value={obj.unloading.row}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v)) return;
                const newUnload = { col: obj.unloading.col, row: v };
                const newRoute = obj.routeAutoMode ? autoRouteBetween(obj.parking, newUnload) : obj.route;
                updateObject(ref, { unloading: newUnload, route: newRoute });
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip
              size="small"
              color={obj.routeAutoMode ? "primary" : "default"}
              label={obj.routeAutoMode ? "経路自動" : "経路手動"}
              onClick={() => {
                const next = !obj.routeAutoMode;
                if (next) {
                  // auto モードへ → 経路を再計算
                  updateObject(ref, { routeAutoMode: true, route: autoRouteBetween(obj.parking, obj.unloading) });
                } else {
                  updateObject(ref, { routeAutoMode: false });
                }
              }}
              clickable
            />
            <Typography variant="caption" color="text.secondary">
              {obj.route.length} ウェイポイント
            </Typography>
          </Stack>
        </>
      )}

      <Divider />

      <Button
        variant="outlined"
        color="error"
        size="small"
        startIcon={<DeleteOutlineRoundedIcon />}
        onClick={() => removeObject(ref)}
      >
        削除
      </Button>
    </Stack>
  );
}

function primaryTileOf(obj: SceneObject): { col: number; row: number } {
  switch (obj.kind) {
    case "conveyor":
      return obj.path[0] ?? { col: 0, row: 0 };
    case "press":
    case "sensor":
      return obj.position;
    case "arm":
      return obj.position;
    case "transport":
      return obj.parking;
  }
}

/** 搬送ロボの経路編集モード切替ボタン群 (P4) */
function TransportRouteEditButtons({ transportId }: { transportId: string }) {
  const routeEditing = useEditorStore((s) => s.routeEditing);
  const beginRouteEdit = useEditorStore((s) => s.beginRouteEdit);
  const finishRouteEdit = useEditorStore((s) => s.finishRouteEdit);
  const cancelRouteEdit = useEditorStore((s) => s.cancelRouteEdit);

  const isEditingThis = routeEditing?.transportId === transportId;
  const partialLength = isEditingThis ? routeEditing.partial.length : 0;

  if (!isEditingThis) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Button size="small" variant="outlined" onClick={() => beginRouteEdit(transportId)}>
          経路をマップで編集
        </Button>
        <Typography variant="caption" color="text.secondary">
          (タイルをクリックで waypoint 追加)
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Button size="small" variant="contained" color="primary" disabled={partialLength < 2} onClick={finishRouteEdit}>
        確定 ({partialLength})
      </Button>
      <Button size="small" variant="outlined" color="inherit" onClick={cancelRouteEdit}>
        キャンセル
      </Button>
    </Stack>
  );
}
